import EMPDataCard from "../../shared/components/EMPDataCard/EMPDataCard";
import EMPBanner from "../../shared/components/EMPBanner/EMPBanner";
import React, { useContext } from "react";
import PropTypes from "prop-types";
import { Box, Typography, Grid } from "@mui/material";
import CancelPurchaseRequest from "../../shared/components/EMPModal/ModalFragments/CancelPurchaseRequest";
import PricingSummary from "../../components/customerHandling/purchaseDetails/addOns/PricingSummary";
import { useQuery } from "@tanstack/react-query";
import { pricingStackQueryOptions } from "../../shared/hooks/UsePricingStack";
import { UserContext } from "../../shared/context/userContext";
import { ModalContext } from "../../shared/components/EMPModal/ModalContext";
import EMPButton from "../../shared/components/EMPButton/EMPButton";
import EMPLoader from "../../shared/components/EMPLoader/EMPLoader";
import {
  canCancelPurchaseRequest,
  isPurchaseRequestAccepted,
} from "../../shared/rules/PurchaseRequestRules";
import { useTranslation } from "react-i18next";
import { formatPriceValue } from "../../utils/FormatPrice";
import {
  LincolnDetailsCard as LincolnPricingCard,
  extractPurchaseInfo,
  getPurchaseRequestDetails,
} from "./PurchaseRequestDetails";
import PreOrderPricingSummary, {
  preOrderPricingSummaryType,
} from "./preorders/PreOrderPricingSummary";

const lincolnHeadingSx = {
  fontFamily: "ProximaNovaSemibold, Arial, sans-serif",
  fontSize: "24px",
  fontWeight: 600,
  lineHeight: "32px",
};

const extractIncentivePrograms = (content) => {
  const { qualifiedIncentiveTypes, purchaseInfo } = content.customerReservation;
  return {
    axzPlan: purchaseInfo ? purchaseInfo.axzPlanPinValidity : null,
    activeMilitary: qualifiedIncentiveTypes?.includes("Affinity - Military"),
    collegeStudent: qualifiedIncentiveTypes?.includes(
      "Affinity - College Student"
    ),
    farmBureauMember: qualifiedIncentiveTypes?.includes(
      "Affinity - Farm Bureau"
    ),
    firstResponder: qualifiedIncentiveTypes?.includes(
      "Affinity - First Responder"
    ),
  };
};

const extractConquestIncentives = (customerReservation) => {
  const items = customerReservation?.items || [];
  return items.flatMap(
    (item) => item?.offers?.offersInfo?.conquestIncentives || []
  );
};

const normalizeOffer = (offer = {}) => {
  const normalizedProgramId = offer?.programId ?? offer?.program_number ?? "";
  const normalizedAmount = Number(offer?.amount ?? offer?.incentiveAmount ?? 0);
  const hasProgramId =
    normalizedProgramId !== null &&
    normalizedProgramId !== undefined &&
    normalizedProgramId !== "";

  return {
    name:
      offer?.name ??
      offer?.displayName ??
      (hasProgramId ? String(normalizedProgramId) : "--"),
    programId: hasProgramId ? String(normalizedProgramId) : "--",
    amount: Number.isFinite(normalizedAmount) ? normalizedAmount : 0,
  };
};

const renderPricingCard = (isLincolnBrand, data, testId) =>
  isLincolnBrand ? (
    <LincolnPricingCard data={data} testId={testId} />
  ) : (
    <EMPDataCard data={data} />
  );

const Pricing = ({ content, orderId }) => {
  const { user } = useContext(UserContext);
  const isLincolnBrand = user.brand === "L";
  const headingSx = {
    color: "text.primary",
    ...(isLincolnBrand && lincolnHeadingSx),
  };
  const userCommonId = `${user.market}|${user.dealerId}|${user.salesCode}|${user.brand}`;
  const { handleModal } = useContext(ModalContext);
  const { t } = useTranslation();

  const pricingStackQuery = useQuery({
    ...pricingStackQueryOptions(orderId, user.market, userCommonId),
    enabled: !!orderId,
    select: ({ data }) => data,
  });

  const pricingStackLoading =
    pricingStackQuery.isPending || pricingStackQuery.isLoading;
  const fetchedPricingStack = pricingStackQuery.data;
  const customerReservation = content.customerReservation;
  const pricingStack = customerReservation?.pricingStack || fetchedPricingStack;
  const isPreOrder = customerReservation?.journey?.businessType === "PREORDER";

  const getOffers = () => {
    const standardOffers = (pricingStack?.offersList || []).map(normalizeOffer);
    const conquestIncentives =
      extractConquestIncentives(customerReservation).map(normalizeOffer);
    const seenOffers = new Set();

    const combinedNormalizedOffers = [
      ...standardOffers,
      ...conquestIncentives,
    ].filter((offer) => {
      const dedupeKey = JSON.stringify([
        offer.programId,
        offer.name,
        offer.amount,
      ]);
      if (seenOffers.has(dedupeKey)) {
        return false;
      }

      seenOffers.add(dedupeKey);
      return true;
    });

    if (combinedNormalizedOffers.length > 0) {
      return combinedNormalizedOffers.map((offer, index) => ({
        key: JSON.stringify([offer.programId, offer.name, offer.amount, index]),
        data: [
          {
            heading: "Incentive",
            content: offer.name,
          },
          {
            heading: "Program Number",
            content: offer.programId,
          },
          {
            heading: "Incentive Amount",
            content: formatPriceValue(offer.amount),
          },
        ],
      }));
    }
    return [];
  };

  const purchaseInfo = extractPurchaseInfo(content);
  const affinityPrograms = extractIncentivePrograms(content);
  const isTaxCreditEligible = customerReservation?.isFederalTaxCreditEligible;
  const purchaseRequestDetails = getPurchaseRequestDetails(content, t, {
    includeStatusDetails: !isLincolnBrand,
  });

  const affinityProgramDetails = [
    {
      heading: "AXZ Plan",
      content: affinityPrograms.axzPlan === "Valid" ? "Yes" : "No",
    },
    {
      heading: "Active Military",
      content: affinityPrograms.activeMilitary ? "Yes" : "No",
    },
    {
      heading: "College Student",
      content: affinityPrograms.collegeStudent ? "Yes" : "No",
    },
    {
      heading: "Farm Bureau Member",
      content: affinityPrograms.farmBureauMember ? "Yes" : "No",
    },
    {
      heading: "First Responder",
      content: affinityPrograms.firstResponder ? "Yes" : "No",
    },
  ];

  const offers = getOffers();

  return (
    <Box
      display="grid"
      gridTemplateColumns="repeat(12, 1fr)"
      columnGap={4}
      rowGap={4}
      sx={{ mt: isLincolnBrand ? 0 : 5, mb: 11 }}
    >
      {!isLincolnBrand && isTaxCreditEligible && (
        <Box
          gridColumn={{
            xs: "span 12",
          }}
        >
          <EMPBanner
            title={t("common.banners.evTaxCredit.title")}
            content={t("common.banners.evTaxCredit.content")}
            hasLink
            linkText={t("common.banners.evTaxCredit.linkText")}
            href="https://www.irs.gov/credits-deductions/credits-for-new-clean-vehicles-purchased-in-2023-or-after"
          />
        </Box>
      )}

      {purchaseRequestDetails.length > 0 && (
        <>
          <Box
            gridColumn={{
              xs: "span 12",
            }}
          >
            <Grid
              container
              alignItems="center"
              justifyContent="space-between"
              rowGap={isLincolnBrand ? 2 : undefined}
            >
              <Grid item>
                <Typography variant="h3" sx={headingSx}>
                  {t("pricing.purchaseRequestDetails.heading")}
                </Typography>
              </Grid>
              <Grid item>
                {canCancelPurchaseRequest() &&
                  isPurchaseRequestAccepted(purchaseInfo) && (
                    <Box>
                      <EMPButton
                        testId="cancel-purchase-request-button"
                        variant={isLincolnBrand ? "text" : "contained"}
                        sx={
                          isLincolnBrand
                            ? {
                                color: "#485B65",
                                backgroundColor: "transparent",
                                border: "none",
                                borderRadius: 0,
                                minWidth: 0,
                                px: 0,
                                "&:hover": { backgroundColor: "transparent" },
                                "&.Mui-focusVisible": {
                                  outline: "2px solid #485B65",
                                  outlineOffset: 3,
                                },
                              }
                            : undefined
                        }
                        onClick={() => {
                          handleModal(
                            t("common.buttonCtas.cancelPurchaseRequest"),
                            <CancelPurchaseRequest orderId={orderId} />
                          );
                        }}
                      >
                        {t("common.buttonCtas.cancelPurchaseRequest")}
                      </EMPButton>
                    </Box>
                  )}
              </Grid>
            </Grid>
          </Box>
          <Box
            gridColumn={{
              xs: "span 12",
            }}
          >
            {renderPricingCard(
              isLincolnBrand,
              purchaseRequestDetails,
              "pricing-purchase-details"
            )}
          </Box>
        </>
      )}
      <Box
        gridColumn={{
          xs: "span 12",
        }}
        sx={{ mt: isLincolnBrand ? 8 : 2 }}
      >
        <Typography variant="h3" sx={headingSx}>
          {t("OrderDetailsPricingSummary.recognitionIncentives")}
        </Typography>
      </Box>
      <Box
        gridColumn={{
          xs: "span 12",
        }}
      >
        {renderPricingCard(
          isLincolnBrand,
          affinityProgramDetails,
          "pricing-incentives"
        )}
      </Box>
      {!isPreOrder && pricingStackLoading && (
        <Box gridColumn={{ xs: "span 12" }} sx={{ height: "120px" }}>
          <Grid container alignItems="center" sx={{ height: "100%" }}>
            <Grid item>
              <EMPLoader loadingMessage="Loading Pricing Stack..." />
            </Grid>
          </Grid>
        </Box>
      )}
      {offers.length > 0 && (
        <>
          <Box gridColumn={{ xs: "span 12" }} sx={{ mt: 2 }}>
            <Typography variant="h3" sx={headingSx}>
              Offers & Incentives
            </Typography>
          </Box>

          {offers.map((offer) => {
            return (
              <Box
                key={offer.key}
                gridColumn={{
                  xs: "span 12",
                }}
              >
                {renderPricingCard(isLincolnBrand, offer.data)}
              </Box>
            );
          })}
        </>
      )}

      {(isPreOrder || pricingStack) && (
        <Box
          gridColumn={{
            xs: "span 12",
          }}
          sx={isLincolnBrand ? { mt: 8 } : undefined}
        >
          {isPreOrder ? (
            <PreOrderPricingSummary
              pricingSummary={customerReservation?.pricingSummary}
            />
          ) : (
            <PricingSummary
              isLincolnOrderDetails={isLincolnBrand}
              isDownPaymentEnabled={
                customerReservation?.purchaseInfo?.isDownPaymentEnabled
              }
              paymentType={customerReservation?.paymentType}
              customerReservation={customerReservation}
              pricingStack={pricingStack}
              isOrder={customerReservation?.orderType}
              isPurchaseRequest
              amountFinanced={
                customerReservation?.financeDataQuote?.amountFinanced ||
                customerReservation?.financeDataQuote
                  ?.totalAmountFinancedStandard
              }
            />
          )}
        </Box>
      )}
    </Box>
  );
};

Pricing.propTypes = {
  content: PropTypes.shape({
    customerReservation: PropTypes.shape({
      journey: PropTypes.shape({ businessType: PropTypes.string }),
      pricingSummary: preOrderPricingSummaryType,
      pricingStack: PropTypes.shape({
        offersList: PropTypes.arrayOf(
          PropTypes.shape({
            name: PropTypes.string,
            programId: PropTypes.oneOfType([
              PropTypes.string,
              PropTypes.number,
            ]),
            amount: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
          })
        ),
      }),
      items: PropTypes.arrayOf(
        PropTypes.shape({
          offers: PropTypes.shape({
            offersInfo: PropTypes.shape({
              totalIncentives: PropTypes.oneOfType([
                PropTypes.string,
                PropTypes.number,
              ]),
              conquestIncentives: PropTypes.arrayOf(
                PropTypes.shape({
                  name: PropTypes.string,
                  programId: PropTypes.oneOfType([
                    PropTypes.string,
                    PropTypes.number,
                  ]),
                  amount: PropTypes.oneOfType([
                    PropTypes.string,
                    PropTypes.number,
                  ]),
                })
              ),
            }),
          }),
        })
      ),
      purchaseInfo: PropTypes.shape({
        acceptRejectStatus: PropTypes.string,
        purchaseType: PropTypes.string,
        purchaseRequestDealerAction: PropTypes.shape({
          dealerAcceptedDate: PropTypes.string,
          dealerActionUserId: PropTypes.oneOfType([
            PropTypes.string,
            PropTypes.number,
          ]),
          dealerRejectedReason: PropTypes.string,
        }),
        purchaseRequestDate: PropTypes.string,
        soldDate: PropTypes.string,
        stockId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        axzPlanPinValidity: PropTypes.string,
        isDownPaymentEnabled: PropTypes.bool,
      }),
      qualifiedIncentiveTypes: PropTypes.arrayOf(PropTypes.string),
      isFederalTaxCreditEligible: PropTypes.bool,
      paymentType: PropTypes.string,
      subJourneyType: PropTypes.string,
      orderType: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
      financeDataQuote: PropTypes.shape({
        amountFinanced: PropTypes.oneOfType([
          PropTypes.string,
          PropTypes.number,
        ]),
        totalAmountFinancedStandard: PropTypes.oneOfType([
          PropTypes.string,
          PropTypes.number,
        ]),
      }),
    }).isRequired,
  }).isRequired,
  orderId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
};

export default Pricing;
